import { capture } from "~/parent/analytics";
import { showFatal } from "~/parent/fatal";
import type { ProgressReporter } from "~/lib/brobsProgress";

const isDev = import.meta.env.DEV;
const useHotReload = !!import.meta.env.VITE_GAZE_HOT_RELOAD;

/** The gaze side of the two message channels. The other sides are window globals. */
export type GazePorts = { extension: MessagePort; vscode: MessagePort };

export type GazeChannel = {
  postMessage: (
    message: Record<string, unknown>,
    transfer?: Transferable[],
  ) => void;
};

/**
 * Makes the message channels between gaze, the VS Code extension and the VS Code workbench.
 * The VS Code fork and the extension read the `*ToGazePort` globals from `window`.
 */
export function createGazePorts(): GazePorts {
  const extensionChannel = new MessageChannel();
  const vscodeChannel = new MessageChannel();

  window.extensionToGazePort = extensionChannel.port2;
  window.vscodeToGazePort = vscodeChannel.port2;
  window.gazeToExtensionPort = extensionChannel.port1;
  window.gazeToVscodePort = vscodeChannel.port1;

  return { extension: extensionChannel.port1, vscode: vscodeChannel.port1 };
}

// Bridge channel
// Proxies messages between iframe and MessagePort without transferring
// This prevents losing the channel when the iframe hot-reloads in development
function setupBridgePort(
  realPort: MessagePort,
  iframe: HTMLIFrameElement,
  portType: "extension" | "vscode",
) {
  // While the iframe is (re)loading its contentWindow is briefly null. Queue
  // messages that arrive during that window so they aren't dropped, and flush
  // them once the iframe has a contentWindow again.
  const pending: unknown[] = [];

  const postToIframe = (data: unknown) => {
    iframe.contentWindow!.postMessage(
      {
        type: "__bridge_from_port__",
        portType,
        data,
      },
      window.location.origin,
    );
  };

  const flush = () => {
    if (!iframe.contentWindow) {
      return;
    }
    while (pending.length > 0) {
      postToIframe(pending.shift());
    }
  };

  // Forward messages from real port to iframe
  realPort.onmessage = (ev) => {
    if (iframe.contentWindow) {
      flush();
      postToIframe(ev.data);
    } else {
      pending.push(ev.data);
    }
  };
  realPort.start();

  // The iframe gains a contentWindow once it finishes (re)loading; flush then.
  iframe.addEventListener("load", flush);
}

/**
 * Adds the gaze iframe to `root`.
 *
 * The iframe sends `on_html_loaded` each time its page loads. In dev the iframe page can load
 * again without a load of the parent, so `onHtmlLoaded` calls its callback for each message.
 */
export function mountGazeIframe(
  root: HTMLElement,
  ports: GazePorts,
  progress: ProgressReporter,
): {
  iframe: HTMLIFrameElement;
  onHtmlLoaded: (callback: (channel: GazeChannel) => void) => void;
} {
  const wrapper = document.createElement("div");
  Object.assign(wrapper.style, {
    position: "absolute",
    inset: "0",
    overflow: "hidden",
  });

  const iframe = document.createElement("iframe");
  iframe.src = useHotReload ? "/gaze-hotreload" : "/gaze";
  iframe.title = "Gaze Application";
  Object.assign(iframe.style, {
    width: "100%",
    height: "100%",
    border: "none",
    // The iframe shows this until gaze paints. `Colors::DARK.window`, because no theme
    // role has resolved at this point.
    background: "#1c1c1c",
  });
  iframe.addEventListener("error", () => {
    console.error("Failed to load Gaze iframe");
    showFatal("Failed to load Gaze application");
  });

  wrapper.append(iframe);
  root.append(wrapper);

  // The iframe receives the forwarded events of the /events/ws socket, and posts its
  // AgentRequest frames back through window.parent.postMessage.
  window.__eventsWs.__registerIframe(iframe);

  const channel: GazeChannel = {
    postMessage: (message, transfer) =>
      iframe.contentWindow?.postMessage(
        message,
        window.location.origin,
        transfer,
      ),
  };

  const htmlLoadedCallbacks: ((channel: GazeChannel) => void)[] = [];
  let bridgeStarted = false;

  window.addEventListener("message", (event) => {
    // Verify message is from our iframe
    if (event.source !== iframe.contentWindow) {
      return;
    }

    // Bridge messages (forward to appropriate port)
    if (isDev && event.data.type === "__bridge_to_port__") {
      switch (event.data.portType) {
        case "extension":
          ports.extension.postMessage(
            event.data.data,
            event.ports as Transferable[],
          );
          break;
        case "vscode":
          ports.vscode.postMessage(event.data.data);
          break;
        default:
          console.error("Unknown port type: ", event.data.portType);
          break;
      }
      return;
    }

    switch (event.data.type) {
      case "on_html_loaded":
        // The iframe page is ready to receive messages.
        progress("html loaded");
        // Dev keeps the ports in the parent and forwards each message, so that the channels
        // stay open when the iframe page loads again. Start the forwarding only after the
        // iframe page loads, because a message before that goes to the about:blank document.
        if (isDev && !bridgeStarted) {
          bridgeStarted = true;
          setupBridgePort(ports.extension, iframe, "extension");
          setupBridgePort(ports.vscode, iframe, "vscode");
        }
        for (const callback of htmlLoadedCallbacks) callback(channel);
        break;
      case "gaze_ready":
        // Gaze has finished its first render, reveal the iframe
        progress.done();
        window.dispatchEvent(new CustomEvent("gazeInitialized"));
        capture("ide_loading_complete");
        break;
      case "logout":
        window.location.href = "/logout";
        break;
      case "request_focus":
        iframe.focus();
        break;

      default:
        // Iframe now handles VSCode communication directly via MessagePort
        break;
    }
  });

  // Keyboard event router: forwards keyboard events to gaze for shortcut processing.
  // If gaze consumes the shortcut, we prevent the event from reaching the focused HTML element.
  // If gaze doesn't consume it, the event propagates normally to the focused element.
  window.addEventListener(
    "keydown",
    (event: KeyboardEvent) => {
      // Skip if the event target is the iframe itself (gaze handles it directly via canvas)
      if (event.target === iframe || iframe.contains(event.target as Node)) {
        return;
      }

      const isTextInput =
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target as HTMLElement)?.isContentEditable ||
        (event.target as HTMLElement)?.role === "textbox";

      const hasModifiers = event.metaKey || event.ctrlKey || event.altKey;

      // For text inputs, only intercept if there are modifiers (potential shortcuts)
      // Exception: always check Escape key as it's commonly used for closing modals
      if (isTextInput && event.key !== "Escape" && !hasModifiers) {
        return;
      }

      // Check with gaze if this shortcut should be consumed
      const checkShortcut = iframe.contentWindow?.checkShortcut;
      if (!checkShortcut) {
        return;
      }

      const consumed = checkShortcut(
        event.key,
        event.shiftKey,
        event.ctrlKey,
        event.altKey,
        event.metaKey,
      );

      if (consumed) {
        event.preventDefault();
        event.stopPropagation();

        // Dispatch a synthetic keyboard event on the gaze canvas so egui receives it
        const canvas =
          iframe.contentDocument?.getElementById("the_canvas_id");
        if (canvas) {
          canvas.focus();
          const syntheticEvent = new KeyboardEvent("keydown", {
            key: event.key,
            code: event.code,
            keyCode: event.keyCode,
            which: event.which,
            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            altKey: event.altKey,
            metaKey: event.metaKey,
            repeat: event.repeat,
            bubbles: true,
            cancelable: true,
          });
          canvas.dispatchEvent(syntheticEvent);
        }
      }
    },
    { capture: true },
  );

  // Notify Gaze iframe when VSCode gains focus
  document.addEventListener("focusin", (event: FocusEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    // Check if the focused element is inside a VSCode/Monaco container
    const isVscodeElement =
      target.closest(".monaco-workbench") !== null ||
      target.closest(".monaco-editor") !== null;

    if (isVscodeElement) {
      iframe.contentWindow?.postMessage(
        { type: "vscode_focused" },
        window.location.origin,
      );
    }
  });

  // HACK: The canvas in the iframe flashes white when the page reloads for some reason. Doesn't matter what background color it has.
  // Use pagehide instead of beforeunload to only hide when page is actually unloading
  // beforeunload fires even when user cancels the reload, which causes the issue
  window.addEventListener("pagehide", () => {
    iframe.style.opacity = "0";
  });

  return {
    iframe,
    onHtmlLoaded: (callback) => htmlLoadedCallbacks.push(callback),
  };
}
